import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAdminAuth, formatApiErrorDetail } from "@/lib/AdminAuthContext";
import {
  LogOut,
  Package,
  CircleDollarSign,
  Truck,
  Clock,
  CheckCircle2,
  X,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Download,
  Ship,
  ExternalLink,
  MapPin,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "-";

const STATUS_COLORS = {
  pending_payment: "bg-white/[0.04]/[0.08] text-white/85 border border-white/15",
  processing: "bg-brandorange/15 text-brandorange border border-brandorange/40",
  shipped: "bg-brandblue/15 text-brandblue border border-brandblue/40",
  delivered: "bg-emerald-500/15 text-emerald-300 border border-emerald-400/40",
  canceled: "bg-brandpink/15 text-brandpink border border-brandpink/40",
};

const PAYMENT_COLORS = {
  pending: "bg-white/[0.04]/[0.08] text-white/85 border border-white/15",
  paid: "bg-emerald-500/15 text-emerald-300 border border-emerald-400/40",
  unpaid: "bg-brandpink/15 text-brandpink border border-brandpink/40",
};

const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];

export default function AdminOrders() {
  const { user, checking, logout, authHeaders } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when status filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (searchDebounced.trim()) params.set("q", searchDebounced.trim());
      params.set("page", String(page));
      params.set("limit", String(limit));
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/orders?${params.toString()}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setOrders(oRes.data.orders);
      setPagination({ total: oRes.data.total, pages: oRes.data.pages });
      setStats(sRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, statusFilter, searchDebounced, page, limit]);

  const filtered = orders;

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/orders.csv`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `house-rules-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Orders exported");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Export failed");
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-[#080412] flex items-center justify-center font-display text-2xl">Loading…</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#080412] text-white" data-testid="admin-orders-page">
      <header className="border-b border-white/12 bg-[#080412]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center" data-testid="admin-brand-logo">
              <img
                src="/assets/hero-logo.png"
                alt="House Rules"
                className="h-11 md:h-12 w-auto rounded-lg drop-shadow-[0_0_18px_rgba(167,66,255,0.5)]"
              />
            </Link>
            <span className="font-sora text-[9.5px] font-semibold uppercase tracking-[0.28em] text-brandpink border border-brandpink/50 px-2.5 py-1 rounded-full">Admin</span>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            <span className="hidden sm:inline font-sora text-xs text-white/60">{user.email}</span>
            <button
              onClick={logout}
              className="hr-cta h-10 px-4 rounded-full text-white font-sora font-semibold text-[10.5px] uppercase tracking-[0.22em] flex items-center gap-2"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 md:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.32em] text-brandpink mb-3">Dashboard</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] tracking-[-0.01em]" data-testid="admin-orders-headline">
              Orders
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              className="hr-outline h-11 px-4 rounded-full text-white font-sora font-semibold text-[10.5px] uppercase tracking-[0.22em] flex items-center gap-2"
              data-testid="admin-export-csv-button"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={load}
              className="hr-outline h-11 px-4 rounded-full text-white font-sora font-semibold text-[10.5px] uppercase tracking-[0.22em] flex items-center gap-2"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <StatCard icon={<CircleDollarSign size={20} />} label="Revenue (paid)" value={stats ? fmt(stats.revenue) : "-"} color="bg-brandblue text-white" />
          <StatCard icon={<Package size={20} />} label="Paid orders" value={stats ? stats.paid_orders : "-"} />
          <StatCard icon={<Clock size={20} />} label="Pending" value={stats ? stats.pending_orders : "-"} />
          <StatCard icon={<Truck size={20} />} label="Shipped" value={stats ? stats.shipped_orders : "-"} color="bg-brandpink text-white" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <input
              placeholder="Search by order id, email, name, tracking…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 font-sans text-sm bg-white/[0.04] border border-white/12 rounded-lg focus:outline-none focus:border-brandblue"
              data-testid="admin-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 font-sans text-sm bg-white/[0.04] border border-white/12 rounded-lg focus:outline-none focus:border-brandblue"
            data-testid="admin-status-filter"
          >
            <option value="">All statuses</option>
            <option value="pending_payment">Pending payment</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="canceled">Canceled</option>
          </select>
          <span className="font-sans text-sm text-white/60 ml-auto">
            {pagination.total} order{pagination.total !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Orders table */}
        <div className="bg-white/[0.04] border border-white/12 rounded-2xl shadow-[0_0_0_1px_rgba(255,44,140,0.15),0_20px_60px_rgba(123,44,255,0.18)] overflow-hidden">
          {loading ? (
            <div className="p-10 text-center font-sans text-white/60">Loading orders…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center font-sans text-white/60">No orders yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-orders-table">
                <thead className="bg-[#100826] border-b border-white/12">
                  <tr className="font-sans text-xs font-bold uppercase tracking-widest text-left">
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Items</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => (
                    <tr key={o.id} className="border-b border-white/8 hover:bg-white/[0.04]/[0.03]" data-testid="admin-order-row">
                      <td className="px-4 py-3 font-mono text-xs">{o.id.slice(0, 8)}…</td>
                      <td className="px-4 py-3">
                        <div className="font-sans">{o.customer?.name}</div>
                        <div className="font-sans text-xs text-white/60">{o.customer?.email}</div>
                      </td>
                      <td className="px-4 py-3 font-sans text-xs">
                        {o.items?.map((it) => `${it.qty}× ${it.slug}`).join(", ")}
                      </td>
                      <td className="px-4 py-3 font-display">{fmt(o.total)}</td>
                      <td className="px-4 py-3">
                        <Badge cls={PAYMENT_COLORS[o.payment_status]}>{o.payment_status}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge cls={STATUS_COLORS[o.status]}>{(o.status || "").replace("_", " ")}</Badge>
                      </td>
                      <td className="px-4 py-3 font-sans text-xs text-white/60">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelected(o)}
                          className="font-sans text-xs font-bold uppercase tracking-widest text-brandblue hover:text-brandpink"
                          data-testid="admin-order-view"
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="admin-pagination">
            <div className="font-sans text-sm text-white/60">
              Page <span className="font-display">{page}</span> of {pagination.pages}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-2 font-sans text-sm bg-white/[0.04] border border-white/12 rounded-lg focus:outline-none"
                data-testid="admin-page-size"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="hr-cta bg-white/[0.04] font-display text-sm px-3 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-page-prev"
              >
                <ChevronLeft size={16} /> PREV
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="hr-cta bg-white/[0.04] font-display text-sm px-3 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-page-next"
              >
                NEXT <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </main>

      {selected && (
        <OrderDetailDrawer
          order={selected}
          onClose={() => setSelected(null)}
          authHeaders={authHeaders}
          onUpdated={(updated) => {
            setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
            setSelected(updated);
            load();
          }}
        />
      )}
    </div>
  );
}

const StatCard = ({ icon, label, value, color = "bg-white/[0.04]" }) => (
  <div className={`border border-white/12 rounded-2xl p-5 shadow-[0_0_0_1px_rgba(255,44,140,0.15),0_20px_60px_rgba(123,44,255,0.18)] ${color}`}>
    <div className="flex items-center justify-between">
      <div className="font-sans text-xs font-bold uppercase tracking-widest opacity-80">{label}</div>
      {icon}
    </div>
    <div className="font-display text-4xl mt-2">{value}</div>
  </div>
);

const Badge = ({ cls, children }) => (
  <span className={`inline-block font-sans text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${cls || "bg-white/[0.04]/[0.08] text-white/85"}`}>
    {children}
  </span>
);

function OrderDetailDrawer({ order, onClose, authHeaders, onUpdated }) {
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.tracking_carrier || "USPS");
  const [status, setStatus] = useState(order.status || "processing");
  const [notes, setNotes] = useState(order.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/orders/${order.id}`,
        {
          tracking_number: tracking || null,
          tracking_carrier: carrier || null,
          status,
          admin_notes: notes || null,
        },
        { headers: authHeaders() }
      );
      toast.success("Order updated");
      onUpdated(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const ship = order.customer?.shipping || {};

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="admin-order-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl h-full bg-[#0A0518] border-l border-white/12 overflow-y-auto shadow-[-20px_0_60px_rgba(0,0,0,0.6)]">
        <div className="sticky top-0 z-10 bg-[#0A0518]/95 backdrop-blur-xl border-b border-white/12 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-sans text-[10px] font-bold uppercase tracking-widest text-white/60">Order</div>
            <div className="font-mono text-sm">{order.id}</div>
          </div>
          <button onClick={onClose} className="hr-cta bg-white/[0.04] font-display text-base px-2 py-1" data-testid="admin-drawer-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer */}
          <Section title="Customer">
            <KV k="Name" v={order.customer?.name} />
            <KV k="Email" v={order.customer?.email} />
            <KV k="Phone" v={order.customer?.phone} />
            <KV k="Marketing opt-in" v={order.customer?.marketing_opt_in ? "Yes" : "No"} />
          </Section>

          {/* Shipping */}
          <Section title="Ship to">
            <div className="font-sans text-sm leading-relaxed">
              {ship.line1}
              {ship.line2 ? <>, {ship.line2}</> : null}
              <br />
              {ship.city}, {ship.state} {ship.postal_code}
              <br />
              {ship.country}
            </div>
          </Section>

          {/* Items */}
          <Section title="Items">
            <div className="space-y-2">
              {order.items?.map((it) => (
                <div key={it.product_id} className="flex items-center gap-3">
                  {it.image && <img src={it.image} alt="" className="w-10 h-10 rounded-md border border-white/12 object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-sans text-sm truncate">{it.name}</div>
                    <div className="font-sans text-xs text-white/60">Qty {it.qty} · {fmt(it.price)} ea</div>
                  </div>
                  <div className="font-display text-sm">{fmt(it.line_total)}</div>
                </div>
              ))}
              <div className="border-t-2 border-white/12 mt-3 pt-2 flex justify-between font-display text-xl">
                <span>Total</span>
                <span>{fmt(order.total)}</span>
              </div>
            </div>
          </Section>

          {/* Shipping (Easyship) */}
          <ShippingPanel order={order} authHeaders={authHeaders} onUpdated={onUpdated} />

          {/* Tracking */}
          <Section title="Tracking & status">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-sans text-xs font-bold uppercase tracking-widest text-white/70 mb-1 block">Carrier</label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full px-3 py-2 font-sans text-sm bg-white/[0.04] border border-white/12 rounded-lg"
                  data-testid="admin-drawer-carrier"
                >
                  {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="font-sans text-xs font-bold uppercase tracking-widest text-white/70 mb-1 block">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 font-sans text-sm bg-white/[0.04] border border-white/12 rounded-lg"
                  data-testid="admin-drawer-status"
                >
                  <option value="pending_payment">Pending payment</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="canceled">Canceled</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="font-sans text-xs font-bold uppercase tracking-widest text-white/70 mb-1 block">Tracking number</label>
                <input
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="e.g. 1Z999AA10123456784"
                  className="w-full px-3 py-2 font-sans text-sm bg-white/[0.04] border border-white/12 rounded-lg"
                  data-testid="admin-drawer-tracking"
                />
              </div>
              <div className="col-span-2">
                <label className="font-sans text-xs font-bold uppercase tracking-widest text-white/70 mb-1 block">Admin notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 font-sans text-sm bg-white/[0.04] border border-white/12 rounded-lg"
                  data-testid="admin-drawer-notes"
                />
              </div>
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="hr-cta mt-4 text-white font-sora font-semibold text-[11px] uppercase tracking-[0.22em] px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-50"
              data-testid="admin-drawer-save"
            >
              <CheckCircle2 size={16} /> {saving ? "SAVING…" : "SAVE CHANGES"}
            </button>
          </Section>
        </div>
      </div>
    </div>
  );
}

function ShippingPanel({ order, authHeaders, onUpdated }) {
  const [rates, setRates] = useState([]);
  const [selected, setSelected] = useState("");
  const [loadingRates, setLoadingRates] = useState(false);
  const [buying, setBuying] = useState(false);
  const alreadyShipped = Boolean(order.tracking_number);
  const canShip = order.payment_status === "paid" && !alreadyShipped;

  const getRates = async () => {
    setLoadingRates(true);
    setRates([]);
    setSelected("");
    try {
      const res = await axios.post(
        `${API}/admin/orders/${order.id}/shipping/rates`,
        {},
        { headers: authHeaders() }
      );
      const list = res.data.rates || [];
      setRates(list);
      if (list.length === 0) {
        toast.warning("No shipping rates available for this destination");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to fetch rates");
    } finally {
      setLoadingRates(false);
    }
  };

  const buyLabel = async () => {
    if (!selected) return;
    if (!window.confirm("Buy shipping label? This will charge your Easyship account and mark the order shipped.")) return;
    setBuying(true);
    try {
      const res = await axios.post(
        `${API}/admin/orders/${order.id}/shipping/ship`,
        { courier_service_id: selected },
        { headers: authHeaders() }
      );
      toast.success("Label purchased and order marked shipped");
      onUpdated(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Label purchase failed");
    } finally {
      setBuying(false);
    }
  };

  const events = order.shipping_events || [];

  return (
    <Section title="Shipping (Easyship)">
      {!alreadyShipped && !canShip && (
        <div className="font-sans text-sm text-white/60">Order not paid yet - rates are unlocked after payment is confirmed.</div>
      )}

      {canShip && (
        <div className="space-y-3" data-testid="shipping-panel">
          {rates.length === 0 && (
            <button
              onClick={getRates}
              disabled={loadingRates}
              className="hr-cta bg-white/[0.04] font-display text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-50"
              data-testid="shipping-get-rates"
            >
              <Ship size={16} /> {loadingRates ? "FETCHING RATES…" : "GET SHIPPING RATES"}
            </button>
          )}
          {rates.length > 0 && (
            <>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-white/12 rounded-lg p-2 bg-[#100826]/40">
                {rates.map((r) => (
                  <label
                    key={r.courier_service_id}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border-2 ${
                      selected === r.courier_service_id ? "border-brandblue bg-brandblue/10" : "border-transparent hover:bg-white/[0.04]"
                    }`}
                    data-testid="shipping-rate-option"
                  >
                    <input
                      type="radio"
                      name="courier"
                      value={r.courier_service_id}
                      checked={selected === r.courier_service_id}
                      onChange={(e) => setSelected(e.target.value)}
                      className="accent-brandblue"
                    />
                    {r.logo && <img src={r.logo} alt="" className="w-8 h-8 object-contain" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-sans text-sm font-bold truncate">{r.courier_name}</div>
                      <div className="font-sans text-xs text-white/60">
                        {r.min_delivery_time === r.max_delivery_time
                          ? `${r.min_delivery_time} business days`
                          : `${r.min_delivery_time}-${r.max_delivery_time} business days`}
                      </div>
                    </div>
                    <div className="font-display text-lg">
                      {r.currency} {Number(r.total_charge).toFixed(2)}
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={buyLabel}
                  disabled={!selected || buying}
                  className="hr-cta bg-brandpink text-white font-display text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-50"
                  data-testid="shipping-buy-label"
                >
                  <Ship size={16} /> {buying ? "BUYING LABEL…" : "BUY LABEL & SHIP"}
                </button>
                <button
                  onClick={getRates}
                  disabled={loadingRates}
                  className="hr-cta bg-white/[0.04] font-display text-sm px-3 py-2 flex items-center gap-2"
                >
                  <RefreshCw size={14} /> REFRESH
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {alreadyShipped && (
        <div className="space-y-3" data-testid="shipping-shipped">
          <KV k="Carrier" v={order.tracking_carrier} />
          <KV k="Tracking #" v={order.tracking_number} />
          <div className="flex flex-wrap gap-2">
            {order.label_url && (
              <a
                href={order.label_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hr-cta bg-white/[0.04] font-display text-sm px-3 py-2 flex items-center gap-2"
                data-testid="shipping-label-pdf"
              >
                <Download size={14} /> DOWNLOAD LABEL
              </a>
            )}
            {order.tracking_page_url && (
              <a
                href={order.tracking_page_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hr-cta bg-white/[0.04] font-display text-sm px-3 py-2 flex items-center gap-2"
                data-testid="shipping-track-link"
              >
                <ExternalLink size={14} /> TRACK PACKAGE
              </a>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      {events.length > 0 && (
        <div className="mt-5" data-testid="shipping-timeline">
          <div className="font-sans text-[10px] font-bold uppercase tracking-widest text-white/50 mb-2">Timeline</div>
          <ol className="relative border-l border-white/12 pl-4 space-y-3">
            {events.map((e, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-brandpink border border-white/12" />
                <div className="font-sans text-sm font-bold">{e.label || e.status}</div>
                <div className="font-sans text-xs text-white/60 flex items-center gap-1">
                  {e.note && (
                    <>
                      <MapPin size={10} />
                      <span>{e.note}</span>
                      <span>·</span>
                    </>
                  )}
                  <span>{fmtDate(e.at)}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Section>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="font-sans text-xs font-bold uppercase tracking-[0.3em] text-brandblue mb-3">{title}</div>
    {children}
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-gray-100 last:border-b-0">
    <span className="font-sans text-xs uppercase tracking-widest text-white/50">{k}</span>
    <span className="font-sans text-sm text-right">{v || "-"}</span>
  </div>
);
